// 📁 src/components/BlogPost.jsx

import { Link, useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { loadArticles } from "../data/loadArticles";
import { PaperDarkBG } from "../kits/DesignKit";
import Preloader from "../components/Preloader";
import { MonoButton } from "../kits/DesignKit/PatternKit";
import { FaCode } from "react-icons/fa";
import { loadModelViewer } from "../utils/loadModelViewer";
import CollapsibleCodeBlock_screen from "../components/CollapsibleCodeBlock_screen";
import RoughBorder from "../kits/LayoutKit/RoughBorder";
import BlogListBlog from "./BlogListBlog";

// Eigenes Bild-Block-Component mit useRef
function EditorJsImageBlock({ src, alt, i }) {
  const imageRef = useRef();
  return (
    <div key={i} className="my-6 p-4 
      rounded shadow relative" ref={imageRef}>
      <img
        src={src}
        alt={alt}
        className="w-full max-w-xl mx-auto rounded-md "
      />
      {alt && (
        <figcaption className="mt-2 text-xs text-center text-gray-600 font-mono">
          {alt}
        </figcaption>
      )}
      <RoughBorder
        parentRef={imageRef}
        radius={10}
        padding={2}
       color="#AC8E66" 
        strokeWidth={0.3}
        roughness={1}
        bowing={2}
      />
    </div>
  );
}

// Eigenes YouTube-Block-Component mit useRef
function YoutubeBlock({ url, i }) {
  const youtubeRef = useRef();
  let videoId = null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtube.com") || parsed.hostname.includes("youtu.be")) {
      videoId = parsed.searchParams.get("v") || parsed.pathname.split('/').pop();
    }
  } catch {}

  return (
    <div key={i} className="my-4 rounded-lg overflow-hidden relative" ref={youtubeRef}>
      <div className="aspect-video border border-gray-800 
        bg-[#f0ede1]
      pt-2 pb-2 pl-2 pr-2 rounded-lg overflow-hidden">
        {videoId ? (
          <iframe
            className="w-full h-full border border-gray-800 rounded-lg"
            src={`https://www.youtube.com/embed/${videoId}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={`youtube-${i}`}
          />
        ) : (
          <p className="p-4 text-red-500 font-mono">⚠️ Kein Video gefunden. Das ist ja blöd</p>
        )}
      </div>
      <RoughBorder
        parentRef={youtubeRef}
        radius={10}
        padding={2}
       color="#AC8E66" 
        strokeWidth={1.1}
        roughness={1}
        bowing={2}
      />
    </div>
  );
}

function renderEditorJsBlocks(blocks = []) {
  return blocks.map((block, i) => {
    const data = block?.data || {};
    switch (block.type) {
      case "header": {
        const Tag = `h${data.level}`;
        return (
          <PaperDarkBG
            key={i}
            className="mt-8 mb-4 rounded-lg px-4 py-2 relative -top-1 inline-block w-full bg-[#f0ede1]"
          >
            <Tag className="text-sm font-mono text-[#1a1a1a] text-shadow flex items-center gap-x-2">
              <FaCode className="text-brand" />
              {data.text}
            </Tag>
          </PaperDarkBG>
        );
      }
      case "paragraph":
        return (
          <p key={i} className="mb-4 ml-4 leading-relaxed font-mono text-xs">
            {data.text}
          </p>
        );
      case "list": {
        const items = data.items || [];
        if (!items.length) return null;
        const Tag = data.style === "unordered" ? "ul" : "ol";
        return (
          <Tag key={i} className={`ml-6 mb-4 list-${data.style === "unordered" ? "disc" : "decimal"} font-mono text-xs`}>
            {items.map((item, idx) => {
              const text = typeof item === "string" ? item : item.content || item.text || "";
              return <li key={idx}>{text}</li>;
            })}
          </Tag>
        );
      }
      case "quote":
        return (
          <PaperDarkBG key={i} className="my-4 rounded-lg">
            <blockquote className="border-l-4 border-brand pl-4 pr-2 py-4 italic text-xs font-mono text-[#EFEBDC]">
              <p>{data.text}</p>
              {data.caption && (
                <cite className="block text-right font-mono mt-2">
                  — {data.caption}
                </cite>
              )}
            </blockquote>
          </PaperDarkBG>
        );
      case "image": {
        const src = data.file?.url || data.src;
        const alt = data.caption || data.alt || "Bild";
        if (!src) {
          return (
            <div key={i} className="text-red-600 font-mono text-xs my-4">
              ⚠️ Kein Bild gefunden im Block {i}
              <pre>{JSON.stringify(data, null, 2)}</pre>
            </div>
          );
        }
        return <EditorJsImageBlock src={src} alt={alt} i={i} key={i} />;
      }
      case "youtube":
        return <YoutubeBlock url={data.url} i={i} key={i} />;
      case "code":
        return (
          <CollapsibleCodeBlock_screen code={block.data.code} language={block.data.language} key={i} />
        );
      case "cta":
        return (
          <PaperDarkBG key={i} className="my-6 text-center rounded-lg">
            <a
              href={data.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full block"
            >
              <MonoButton className="w-full  text-xs px-2 py-1 pt-2 pb-2 hover: bg-gray-950 text-brand text-shadow">
                {data.text || "Weiter"}
              </MonoButton>
            </a>
          </PaperDarkBG>
        );
      case "delimiter":
        return <hr key={i} className="my-8 border-brand" />;
      case "embed":
        return (
          <PaperDarkBG key={i} className="my-6 rounded-lg overflow-hidden">
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src={data.embed}
                title={data.caption || `Embed-${i}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </PaperDarkBG>
        );
      case "checklist":
        return (
          <PaperDarkBG key={i} className="mb-4 p-4 rounded-lg">
            <ul className="list-none ml-0">
              {data.items.map((item, idx) => (
                <li key={idx} className="flex items-center mb-2">
                  <input type="checkbox" checked={item.checked} readOnly className="mr-2" />
                  <span className="font-mono text-xs text-[#EFEBDC]">{item.text}</span>
                </li>
              ))}
            </ul>
          </PaperDarkBG>
        );
      case "table":
        return (
          <PaperDarkBG key={i} className="mb-6 p-4 rounded-lg overflow-x-auto">
            <table className="table-auto border-collapse w-full text-[#EFEBDC]">
              <tbody>
                {data.content.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c} className="border border-[#AC8E66]/40 px-4 py-2 text-left font-mono text-xs">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </PaperDarkBG>
        );
      case "model3d": {
        const src = data.src;
        const alt = data.alt || "3D Modell";
        if (!src) {
          return (
            <div key={i} className="text-red-600 font-mono text-xs my-4">
              ⚠️ Kein 3D-Modell angegeben im Block {i}
              <pre>{JSON.stringify(data, null, 2)}</pre>
            </div>
          );
        }
        return (
          <div key={i} className="my-6 p-4 rounded-lg filter grayscale">
            <model-viewer
              src={src}
              alt={alt}
              auto-rotate
              camera-controls
              ar
              ios-src={src}
              exposure="1.3"
              shadow-intensity="1"
              type="model/gltf-binary"
              style={{ width: "100%", height: "400px" }}
            />
            <figcaption className="mt-2 text-xs text-center text-gray-600 font-mono">
              {alt || src.split("/").pop()}
            </figcaption>
          </div>
        );
      }
      default:
        return (
          <pre
            key={i}
            className="bg-yellow-100 text-red-600 text-xs p-2 rounded overflow-x-auto font-mono"
          >
            ⚠️ Unbekannter Block-Typ: {block.type}
            {"\n"}
            {JSON.stringify(data, null, 2)}
          </pre>
        );
    }
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref für das große Artikelbild oben
  const imageBoxRef = useRef();

  useEffect(() => {
    async function fetchPost() {
      setIsLoading(true);
      setError(null);

      const fetchedArticle = await loadArticles(slug);

      if (fetchedArticle) {
        setPost(fetchedArticle);
      } else {
        setError("Artikel konnte nicht gefunden werden oder ist ungültig.");
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      setIsLoading(false);
    }

    fetchPost();
  }, [slug]);

  useEffect(() => {
    loadModelViewer();
  }, []);

  return (
    <Preloader isReady={!isLoading}>
      {error ? (
        <div className="text-green-400   text-center text-lg py-10 font-mono">
          {error}
        </div>
      ) : (
        post && (
          <PaperDarkBG className="py-10">
            <div className="max-w-4xl w-full mx-auto pt-4 px-4 font-mono">
              <div className="overflow-hidden  border-b border-brand ">
                <div className="px-3 sm:px-5 pt-3 sm:pt-2">
                  <nav className="flex w-full gap-1 overflow-x-auto no-scrollbar pb-0">
                    <Link
                      to="/zenlab2"
                      className="shrink-0 min-w-[128px] h-9 sm:h-10 px-3 rounded-t-2xl border border-b-0 border-[#2a2a2a] bg-[#1a1a1a] text-[#EFEBDC]/80 hover:border-[#AC8E66]/60 text-[10px] sm:text-[11px] tracking-[0.08em] font-mono flex items-center justify-center"
                    >
                      ZenLab
                    </Link>
                    <Link
                      to="/zenlab2?tab=blog2"
                      className="shrink-0 min-w-[128px] h-9 sm:h-10 px-3 rounded-t-2xl border border-b-0 border-[#2a2a2a] bg-[#1a1a1a] text-[#EFEBDC]/80 hover:border-[#AC8E66]/60 hover:text-[#EFEBDC] text-[10px] sm:text-[11px] tracking-[0.08em] font-mono flex items-center justify-center transition-colors duration-200"
                    >
                      Wissen Blog
                    </Link>
                    <span
                      className="shrink-0 min-w-[128px] h-9 sm:h-10 px-3 rounded-t-2xl border border-b-0 border-[#AC8E66] bg-[#dbd8cc]/95 text-[#111111] shadow-[0_10px_28px_rgba(0,0,0,0.28)] text-[10px] sm:text-[11px] tracking-[0.08em] font-mono flex items-center justify-center"
                      aria-current="page"
                    >
                      {post.title}
                    </span>
                  </nav>
                </div>
              </div>
            </div>

            <article className="max-w-4xl w-full mx-auto mt-2 mb-10 px-4 font-mono">
              <div className="mt-5"></div>

              {/* Blog-Hauptbild mit RoughBorder */}
              {post.image && (
                <div
                  ref={imageBoxRef}
                  className="block w-full md:inline-block md:max-w-prose 
                
                  px-4 py-4 shadow-lg relative -top-1"
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full object-cover rounded-md 
                     "
                  />
                  <RoughBorder
                    parentRef={imageBoxRef}
                    radius={20}
                    padding={2}
                  color="#AC8E66" 
                    strokeWidth={0.3}
                    roughness={1}
                    bowing={2}
                  />
                </div>
              )}

              <div className="mt-4 text-xs">
                {Array.isArray(post.blocks) ? (
                  renderEditorJsBlocks(post.blocks)
                ) : (
                  <p className="whitespace-pre-line">{post.content}</p>
                )}
              </div>
            </article>
            {post.content?.blocks && Array.isArray(post.content.blocks) && (
              <div className="mt-8">
                {renderEditorJsBlocks(post.content.blocks)}
              </div>
            )}
            {post && <BlogListBlog currentSlug={slug} max={3} />}
          </PaperDarkBG>
        )
      )}
    </Preloader>
  );
}
